import {useMemo} from 'react';

/**
 * KVCacheManager - Object-oriented implementation
 *
 * A generic, render-lightweight cache for batching ID→Value loads.
 * Designed to be unit testable and has no React dependency.
 *
 * ## Usage Examples
 *
 * ### Basic Usage (React Hook)
 * ```typescript
 * import {useKVCacheManager} from './KVCacheManager';
 *
 * // In a React component:
 * const kv = useKVCacheManager<string, CallRow, any>({
 *   // Fetch a batch of elements for the given IDs
 *   batchLoad: async (ids) => api.fetchByIds(ids),
 *   // How to extract the key from an element
 *   getId: (row) => row.id,
 *   // How to project the cached value for that key
 *   getValue: (row) => row.output,
 *   // Optional tuning
 *   batchSize: 500,
 *   debounceMs: 10,
 * });
 *
 * // Subscribe to per-ID events (no React re-renders are triggered by the cache)
 * useEffect(() => kv.eventObserver.subscribe(evt => {
 *   // evt: {id, status: 'idle'|'loading'|'loaded'|'error'|'evicted', value?, error?}
 *   // Update local component state as needed
 * }), [kv]);
 *
 * // Request a key when a consumer mounts/comes into view
 * useEffect(() => {
 *   kv.request(id);
 *   return () => kv.evict(id); // Evict when consumer unmounts/leaves view
 * }, [id, kv]);
 * ```
 *
 * ### Direct Usage (Non-React)
 * ```typescript
 * const cache = new KVCacheManagerImpl({
 *   batchLoad: async (ids) => fetchDataByIds(ids),
 *   getId: (item) => item.id,
 *   getValue: (item) => item.value,
 * });
 *
 * // Subscribe to events
 * const unsubscribe = cache.eventObserver.subscribe(event => {
 *   console.log(`ID ${event.id} is now ${event.status}`);
 *   if (event.status === 'loaded') {
 *     console.log('Value:', event.value);
 *   }
 * });
 *
 * // Request data
 * cache.request('some-id');
 *
 * // Clean up
 * cache.evict('some-id');
 * unsubscribe();
 * ```
 *
 * ## Behavior
 * - **Batching**: multiple `request(id)` calls within `debounceMs` are coalesced
 *   into a single `batchLoad` call, split into `batchSize` chunks if needed.
 * - **Status events**: each requested id emits 'loading' then 'loaded'|'error'.
 * - **Mapping**: results are associated to IDs via `getId(el)`, and cached value via
 *   `getValue(el)`; missing IDs emit 'error'.
 * - **Lifecycle**: `request` increments a watcher count; `evict` decrements and
 *   emits 'evicted' when watchers reach 0 (and removes from cache).
 *
 * ## Flow
 * 1. `request(id)` bumps watcher count and schedules a batched load.
 * 2. On flush, all pending IDs are marked `loading` and grouped into batches.
 * 3. `batchLoad` fetches elements; `getId` and `getValue` map them back to
 *    requested IDs, emitting `loaded` or `error` per ID.
 * 4. `evict(id)` decrements watcher count; when it reaches 0, the ID is
 *    removed from cache and an `evicted` event fires.
 */

export type KVLoadEvent<K, V> = {
  id: K;
  status: 'idle' | 'loading' | 'loaded' | 'error' | 'evicted';
  value?: V;
  error?: any;
};

export type KVEventObserver<K, V> = {
  subscribe: (listener: (evt: KVLoadEvent<K, V>) => void) => () => void;
};

export type KVCacheManagerInterface<K, V> = {
  eventObserver: KVEventObserver<K, V>;
  request: (id: K) => void;
  evict: (id: K) => void;
  purgeCache: () => void;
};

type Entry<V> = {
  status: 'idle' | 'loading' | 'loaded' | 'error';
  value?: V;
  error?: any;
  watchers: number;
  lastAccess: number;
};

export type KVCacheManagerOptions<K, E, V> = {
  batchLoad: (ids: readonly K[]) => Promise<readonly E[]>;
  getId: (el: E) => K;
  getValue: (el: E) => V;
  batchSize?: number;
  debounceMs?: number;
};

// Legacy type aliases for backward compatibility
export type {KVCacheManagerInterface as KVCacheManager};
export type UseKVCacheManagerOptions<K, E, V> = KVCacheManagerOptions<K, E, V>;

/**
 * React hook utility for creating a memoized KVCacheManager instance.
 * Use this in React components instead of creating the manager directly.
 *
 * @param options Configuration options for the cache manager
 * @returns A memoized KVCacheManager instance
 */
export function useKVCacheManager<K, E, V>(
  options: KVCacheManagerOptions<K, E, V>
): KVCacheManagerInterface<K, V> {
  // Import React's useMemo dynamically to avoid dependency when not in React context
  return useMemo(() => new KVCacheManagerImpl(options), [options]);
}

export class KVCacheManagerImpl<K, E, V>
  implements KVCacheManagerInterface<K, V>
{
  private cache = new Map<K, Entry<V>>();
  private listeners = new Set<(evt: KVLoadEvent<K, V>) => void>();
  private pending = new Set<K>();
  private timerId: any = null;

  private readonly batchLoad: (ids: readonly K[]) => Promise<readonly E[]>;
  private readonly getId: (el: E) => K;
  private readonly getValue: (el: E) => V;
  private readonly batchSize: number;
  private readonly debounceMs: number;

  public readonly eventObserver: KVEventObserver<K, V>;

  constructor(options: KVCacheManagerOptions<K, E, V>) {
    this.batchLoad = options.batchLoad;
    this.getId = options.getId;
    this.getValue = options.getValue;
    this.batchSize = options.batchSize ?? 500;
    this.debounceMs = options.debounceMs ?? 10;

    this.eventObserver = {
      subscribe: (listener: (evt: KVLoadEvent<K, V>) => void) => {
        this.listeners.add(listener);
        return () => {
          this.listeners.delete(listener);
        };
      },
    };
  }

  private emit(evt: KVLoadEvent<K, V>): void {
    this.listeners.forEach(listener => {
      try {
        listener(evt);
      } catch (_) {
        // Swallow listener errors to avoid breaking unrelated consumers
      }
    });
  }

  private scheduleFlush(): void {
    if (this.timerId != null) {
      return;
    }

    this.timerId = setTimeout(async () => {
      this.timerId = null;
      const idsToLoad: K[] = Array.from(new Set(Array.from(this.pending)));
      this.pending.clear();

      if (idsToLoad.length === 0) {
        return;
      }

      // Mark as loading and emit events
      idsToLoad.forEach(id => {
        const prev = this.cache.get(id);
        const next: Entry<V> = {
          status: 'loading',
          watchers: prev?.watchers ?? 0,
          lastAccess: Date.now(),
          value: prev?.value,
          error: undefined,
        };
        this.cache.set(id, next);
        this.emit({id, status: 'loading'});
      });

      // Split into batches
      const batches: K[][] = [];
      for (let i = 0; i < idsToLoad.length; i += this.batchSize) {
        batches.push(idsToLoad.slice(i, i + this.batchSize));
      }

      try {
        const results = await Promise.all(batches.map(b => this.batchLoad(b)));
        const flat = results.flat();
        const byId = new Map<K, V>();
        flat.forEach(el => {
          const id = this.getId(el);
          byId.set(id, this.getValue(el));
        });

        idsToLoad.forEach(id => {
          if (byId.has(id)) {
            const entry: Entry<V> = {
              status: 'loaded',
              value: byId.get(id),
              watchers: this.cache.get(id)?.watchers ?? 0,
              lastAccess: Date.now(),
            };
            this.cache.set(id, entry);
            this.emit({id, status: 'loaded', value: entry.value});
          } else {
            const e: Entry<V> = {
              status: 'error',
              error: new Error('Not found'),
              watchers: this.cache.get(id)?.watchers ?? 0,
              lastAccess: Date.now(),
            };
            this.cache.set(id, e);
            this.emit({id, status: 'error', error: e.error});
          }
        });
      } catch (err) {
        idsToLoad.forEach(id => {
          const e: Entry<V> = {
            status: 'error',
            error: err,
            watchers: this.cache.get(id)?.watchers ?? 0,
            lastAccess: Date.now(),
          };
          this.cache.set(id, e);
          this.emit({id, status: 'error', error: err});
        });
      }
    }, this.debounceMs);
  }

  /**
   * Requests data for the given ID, incrementing its watcher count.
   *
   * If the data is already loaded, immediately emits a 'loaded' event.
   * If the data is currently loading, does nothing (existing load will complete).
   * Otherwise, schedules the ID for batched loading and emits 'loading' → 'loaded'/'error' events.
   *
   * @param id The ID to request data for
   */
  public request(id: K): void {
    const entry = this.cache.get(id);
    const nextWatchers = (entry?.watchers ?? 0) + 1;
    this.cache.set(id, {
      status: entry?.status ?? 'idle',
      value: entry?.value,
      error: entry?.error,
      watchers: nextWatchers,
      lastAccess: Date.now(),
    });

    if (entry?.status === 'loaded') {
      this.emit({id, status: 'loaded', value: entry.value});
      return;
    }
    if (entry?.status === 'loading') {
      return;
    }
    this.pending.add(id);
    this.scheduleFlush();
  }

  /**
   * Decrements the watcher count for the given ID.
   *
   * When the watcher count reaches 0, the entry is removed from cache and an 'evicted' event is emitted.
   * This is typically called when a component unmounts or no longer needs the data.
   *
   * @param id The ID to evict/decrement watchers for
   */
  public evict(id: K): void {
    const entry = this.cache.get(id);
    if (!entry) {
      return;
    }
    const nextWatchers = Math.max(0, (entry.watchers ?? 0) - 1);
    if (nextWatchers === 0) {
      this.cache.delete(id);
      this.pending.delete(id);
      this.emit({id, status: 'evicted'});
      return;
    }
    const next: Entry<V> = {
      ...entry,
      watchers: nextWatchers,
      lastAccess: Date.now(),
    };
    this.cache.set(id, next);
  }

  /**
   * Clears all cached data and cancels any pending batch operations.
   *
   * Emits 'evicted' events for all cached entries and resets the cache to empty state.
   * Useful for cleanup or when you need to force reload all data.
   */
  public purgeCache(): void {
    if (this.timerId != null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
    Array.from(this.cache.keys()).forEach(id => {
      this.emit({id, status: 'evicted'});
    });
    this.cache.clear();
    this.pending.clear();
  }
}
